import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { FaCheck, FaUpload } from "react-icons/fa";
import privateAxios from "../../utils/axiosPrivate";
import {
  createPartnerCustomerURL,
  uploadPartnerCustomerDocsURL
} from "../../utils/constants/apiEndPoints";
import { PENDING, CUSTOMER_DD } from "../../utils/constants/appConstants";
import { addClientModal } from "../../locales/en";
import "./addCustomerModal.scss";

// Supporting-document types offered for a new client's financial docs. Each value must
// match a DocumentTypeEnum @JsonValue string on the backend EXACTLY (note the double
// space in "Accounts -  Profit and Loss") or the upload is rejected. These are wire
// values, not display copy, so they stay out of the locale file.
const DOC_TYPES = [
  "Bank Statements",
  "Accounts -  Profit and Loss",
  "Accounts - Accounts Receivable",
  "Invoices",
  "Others"
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Lenient international phone: an optional leading +, then at least 6 digits,
// allowing spaces, dashes and parentheses between them.
const PHONE_RE = /^\+?[\d][\d\s()-]{5,}$/;

const REQUIRED_FIELDS = ["businessName", "firstName", "lastName", "email"];

function fieldError(key, value) {
  const v = (value ?? "").trim();
  const errMsg = addClientModal.errMsg;
  switch (key) {
    case "businessName":
      return v ? "" : errMsg.businessNameRequired;
    case "firstName":
      return v ? "" : errMsg.firstNameRequired;
    case "lastName":
      return v ? "" : errMsg.lastNameRequired;
    case "email":
      if (!v) return errMsg.emailRequired;
      return EMAIL_RE.test(v) ? "" : errMsg.emailInvalid;
    case "phone":
      if (!v) return "";
      return PHONE_RE.test(v) ? "" : errMsg.phoneInvalid;
    default:
      return "";
  }
}

export default function AddCustomerModal({
  signupUrl,
  signupCopyUrl,
  onClose,
  onSuccess
}) {
  const [form, setForm] = useState({
    businessName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    notes: ""
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  // Financial documents queued for upload once the client account exists.
  const [docs, setDocs] = useState([]);
  const [docWarning, setDocWarning] = useState("");
  const fileInputRef = useRef(null);
  const docIdRef = useRef(0);

  const { labels, placeholders, upload, buttons, errMsg, success } =
    addClientModal;

  const openPicker = () => fileInputRef.current?.click();
  const onFilesPicked = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) {
      setDocs((prev) => [
        ...prev,
        ...picked.map((file) => {
          docIdRef.current += 1;
          return { id: `doc-${docIdRef.current}`, file, type: "Others" };
        })
      ]);
    }
    // Reset so picking the same file again still fires onChange.
    e.target.value = "";
  };
  const removeDoc = (id) => () =>
    setDocs((prev) => prev.filter((d) => d.id !== id));
  const changeDocType = (id) => (e) => {
    const { value } = e.target;
    setDocs((prev) =>
      prev.map((d) => (d.id === id ? { ...d, type: value } : d))
    );
  };

  const uploadDocs = async (customerUserAccountId) => {
    const fd = new FormData();
    // Partner-scoped endpoint: the backend checks the client belongs to this partner
    // and files/documentTypes are paired by index.
    docs.forEach((d) => {
      fd.append("files", d.file);
      fd.append("documentTypes", d.type);
    });
    await privateAxios.post(
      uploadPartnerCustomerDocsURL(customerUserAccountId),
      fd,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  };

  const validatedFields = [...REQUIRED_FIELDS, "phone"];
  const hasErrors = validatedFields.some((k) => fieldError(k, form[k]));
  const canSubmit = !hasErrors && !submitting;

  const update = (k) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [k]: value }));
  };
  const markTouched = (k) => () =>
    setTouched((prev) => ({ ...prev, [k]: true }));
  // Show a field's error only once it's been touched (or after a submit attempt
  // marks everything touched), so the form doesn't shout on first render.
  const shownError = (k) => (touched[k] ? fieldError(k, form[k]) : "");

  const copyLink = () => {
    if (navigator.clipboard?.writeText && signupCopyUrl) {
      navigator.clipboard.writeText(signupCopyUrl).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  async function submit(mode) {
    // A submit attempt reveals every field's validation state.
    setTouched({
      businessName: true,
      firstName: true,
      lastName: true,
      email: true,
      phone: true
    });
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // mode=link → customer gets the set-password email.
      // mode=preassess → customer email suppressed; info@fundtap.co gets an internal alert.
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        businessName: form.businessName.trim(),
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
        mode
      };

      const res = await privateAxios.post(createPartnerCustomerURL, payload);
      // Attach any queued documents to the freshly created client so they show up
      // in the client's Owners tab. A failure here must not undo the created client,
      // so it only raises a soft warning on the success screen.
      const newId = res?.data?.data?.userAccountId;
      if (docs.length) {
        if (!newId) {
          setDocWarning(errMsg.docsNoId);
        } else {
          try {
            await uploadDocs(newId);
          } catch (docErr) {
            const status = docErr?.response?.status;
            const detail = docErr?.response?.data?.message;
            const reason = [status, detail].filter(Boolean).join(" ");
            setDocWarning(
              `${errMsg.docsUploadFailed}${reason ? ` (${reason})` : "."}`
            );
          }
        }
      }
      setSubmitted(mode);
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(e?.response?.data?.message || errMsg.createFailed);
    } finally {
      setSubmitting(false);
    }
  }

  const required = <span className="add-customer-modal__required">*</span>;

  // A plain render helper (not a nested component) so ESLint's
  // no-unstable-nested-components rule stays happy.
  const renderFieldError = (name) => {
    const msg = shownError(name);
    return msg ? (
      <div className="add-customer-modal__error-text">{msg}</div>
    ) : null;
  };

  const inputClassFor = (k) =>
    shownError(k)
      ? "add-customer-modal__input has-error"
      : "add-customer-modal__input";

  const businessName = form.businessName.trim();

  return (
    <div className="add-customer-modal-overlay" onClick={onClose}>
      <div className="add-customer-modal" onClick={(e) => e.stopPropagation()}>
        {submitted ? (
          <div className="add-customer-modal__success">
            <div className="add-customer-modal__success-icon">
              <FaCheck size={22} />
            </div>
            <div className="add-customer-modal__success-title">
              {submitted === "link"
                ? success.linkTitle
                : success.preassessTitle}
            </div>
            <div className="add-customer-modal__success-body">
              {submitted === "link" ? (
                <>
                  {success.linkEmailedPrefix}{" "}
                  <b className="add-customer-modal__success-strong">
                    {form.email}
                  </b>{" "}
                  {success.linkEmailedMiddle} {businessName}{" "}
                  {success.linkShowUnder}{" "}
                  <b className="add-customer-modal__success-strong">
                    {PENDING}
                  </b>{" "}
                  {success.clientBook}
                </>
              ) : (
                <>
                  {success.preassessReceivedPrefix} {businessName}
                  {success.preassessReceivedSuffix}{" "}
                  <b className="add-customer-modal__success-strong">
                    {PENDING}
                  </b>{" "}
                  {success.preassessMoveInto}{" "}
                  <b className="add-customer-modal__success-strong">
                    {CUSTOMER_DD}
                  </b>{" "}
                  {success.preassessReviewSuffix}
                </>
              )}
            </div>
            {docWarning && (
              <div className="add-customer-modal__doc-warning">
                {docWarning}
              </div>
            )}
            <button onClick={onClose} className="add-customer-modal__done-btn">
              {buttons.done}
            </button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit("link");
            }}
          >
            <div className="add-customer-modal__header">
              <div className="add-customer-modal__header-row">
                <div>
                  <div className="add-customer-modal__title">
                    {addClientModal.title}
                  </div>
                  <div className="add-customer-modal__subtitle">
                    {addClientModal.subtitle}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="add-customer-modal__close-btn"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Share-link shortcut */}
            <div className="add-customer-modal__share">
              <div className="add-customer-modal__share-info">
                <div className="add-customer-modal__share-label">
                  {addClientModal.shareLabel}
                </div>
                <div className="add-customer-modal__share-url">{signupUrl}</div>
              </div>
              <button
                type="button"
                onClick={copyLink}
                className={`add-customer-modal__copy-btn${copied ? " is-copied" : ""}`}
              >
                {copied ? (
                  <>
                    <FaCheck size={12} /> {addClientModal.copied}
                  </>
                ) : (
                  addClientModal.copyLink
                )}
              </button>
            </div>

            <div className="add-customer-modal__body">
              <div>
                <label className="add-customer-modal__label">
                  {labels.businessName} {required}
                </label>
                <input
                  value={form.businessName}
                  onChange={update("businessName")}
                  onBlur={markTouched("businessName")}
                  required
                  placeholder={placeholders.businessName}
                  className={inputClassFor("businessName")}
                />
                {renderFieldError("businessName")}
              </div>

              <div className="add-customer-modal__row-2col">
                <div>
                  <label className="add-customer-modal__label">
                    {labels.firstName} {required}
                  </label>
                  <input
                    value={form.firstName}
                    onChange={update("firstName")}
                    onBlur={markTouched("firstName")}
                    required
                    placeholder={placeholders.firstName}
                    className={inputClassFor("firstName")}
                  />
                  {renderFieldError("firstName")}
                </div>
                <div>
                  <label className="add-customer-modal__label">
                    {labels.lastName} {required}
                  </label>
                  <input
                    value={form.lastName}
                    onChange={update("lastName")}
                    onBlur={markTouched("lastName")}
                    required
                    placeholder={placeholders.lastName}
                    className={inputClassFor("lastName")}
                  />
                  {renderFieldError("lastName")}
                </div>
              </div>

              <div className="add-customer-modal__row-email-phone">
                <div>
                  <label className="add-customer-modal__label">
                    {labels.email} {required}
                  </label>
                  <input
                    value={form.email}
                    onChange={update("email")}
                    onBlur={markTouched("email")}
                    required
                    type="email"
                    placeholder={placeholders.email}
                    className={inputClassFor("email")}
                  />
                  {renderFieldError("email")}
                </div>
                <div>
                  <label className="add-customer-modal__label">
                    {labels.phone}
                  </label>
                  <input
                    value={form.phone}
                    onChange={update("phone")}
                    onBlur={markTouched("phone")}
                    placeholder={placeholders.phone}
                    className={inputClassFor("phone")}
                  />
                  {renderFieldError("phone")}
                </div>
              </div>

              <div>
                <label className="add-customer-modal__label">
                  {labels.documents}{" "}
                  <span className="add-customer-modal__label-optional">
                    {labels.optional}
                  </span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.csv,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={onFilesPicked}
                  className="add-customer-modal__file-input"
                />
                {docs.length === 0 ? (
                  <button
                    type="button"
                    onClick={openPicker}
                    className="add-customer-modal__upload-btn"
                  >
                    <div className="add-customer-modal__upload-icon">
                      <FaUpload size={16} />
                    </div>
                    <div className="add-customer-modal__upload-text">
                      <div className="add-customer-modal__upload-title">
                        {upload.title}
                      </div>
                      <div className="add-customer-modal__upload-hint">
                        {upload.hint}
                      </div>
                    </div>
                  </button>
                ) : (
                  <div className="add-customer-modal__doc-list">
                    {docs.map((d) => (
                      <div key={d.id} className="add-customer-modal__doc-item">
                        <span className="add-customer-modal__doc-name">
                          {d.file.name}
                        </span>
                        <select
                          value={d.type}
                          onChange={changeDocType(d.id)}
                          className="add-customer-modal__doc-type-select"
                        >
                          {DOC_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={removeDoc(d.id)}
                          aria-label={upload.removeDocument}
                          className="add-customer-modal__doc-remove-btn"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={openPicker}
                      className="add-customer-modal__doc-add-more-btn"
                    >
                      {upload.addMore}
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="add-customer-modal__label">
                  {labels.notes}
                </label>
                <textarea
                  value={form.notes}
                  onChange={update("notes")}
                  rows={3}
                  placeholder={placeholders.notes}
                  className="add-customer-modal__input add-customer-modal__notes"
                />
              </div>

              {error && (
                <div className="add-customer-modal__form-error">{error}</div>
              )}
            </div>

            <div className="add-customer-modal__footer">
              <button
                type="button"
                onClick={onClose}
                className="add-customer-modal__cancel-btn"
              >
                {buttons.cancel}
              </button>
              <div className="add-customer-modal__actions">
                <button
                  type="button"
                  onClick={() => submit("link")}
                  disabled={!canSubmit}
                  className="add-customer-modal__link-btn"
                >
                  {submitting ? buttons.sending : buttons.sendLink}
                </button>
                <button
                  type="button"
                  onClick={() => submit("preassess")}
                  disabled={!canSubmit}
                  className="add-customer-modal__preassess-btn"
                >
                  {buttons.sendPreassessment}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

AddCustomerModal.propTypes = {
  signupUrl: PropTypes.string,
  signupCopyUrl: PropTypes.string,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
};
